import { HttpClientXsrfModule } from '@angular/common/http';
import { ErrorHandler, NgModule, PLATFORM_ID } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  BrowserModule,
  BrowserTransferStateModule,
} from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  ABTest,
  ABTestOverrides,
  ABTEST_WHITELIST_COOKIE,
} from '@freelancer/abtest';
import { ABTestTesting } from '@freelancer/abtest/testing';
import { Auth, AuthModule, AUTH_CONFIG } from '@freelancer/auth';
import { AuthTesting } from '@freelancer/auth/testing';
import { BILLING_CONFIG } from '@freelancer/billing';
import { CAPTCHA_CONFIG } from '@freelancer/captcha';
import { ComponentsModalsModule } from '@freelancer/components/components-modals.module';
import { FileViewerModule } from '@freelancer/components/file-viewer';
import {
  APPS_DOMAINS_MAP,
  APP_NAME,
  ENVIRONMENT_NAME,
  SITE_NAME,
  USE_FAKES,
} from '@freelancer/config';
import {
  CustomFieldsValidator,
  CustomFieldsValidatorTesting,
} from '@freelancer/custom-fields';
import {
  datastoreActionSanitizer,
  DatastoreModule,
} from '@freelancer/datastore';
import { datastoreInitializer } from '@freelancer/datastore/collections-testing';
import { DatastoreFakeModule } from '@freelancer/datastore/testing';
import { FACEBOOK_CONFIG } from '@freelancer/facebook';
import { FileDownloadModule } from '@freelancer/file-download';
import { FileUploadLegacyModule } from '@freelancer/file-upload-legacy';
import {
  FreelancerHttp,
  FREELANCER_HTTP_ABTEST_OVERRIDES_PROVIDER,
  FREELANCER_HTTP_AUTH_PROVIDERS,
  FREELANCER_HTTP_CONFIG,
  FREELANCER_HTTP_TRACKING_PROVIDER,
  SENTRY_TRACING_PROVIDER,
} from '@freelancer/freelancer-http';
import { BackgroundGeolocation } from '@freelancer/geolocation';
import { GoogleMapsModule, GOOGLE_MAPS_CONFIG } from '@freelancer/google-maps';
import { InAppBrowser, InAppBrowserModule } from '@freelancer/in-app-browser';
import { LANGUAGE_COOKIE } from '@freelancer/localization';
import {
  FREELANCER_LOCATION_AUTH_PROVIDER,
  FREELANCER_LOCATION_HTTP_BASE_URL_PROVIDER,
  FREELANCER_LOCATION_IN_APP_BROWSER_PROVIDER,
  FREELANCER_LOCATION_PWA_PROVIDER,
  FREELANCER_LOCATION_TRACKING_CONSENT_PROVIDER,
  Location,
} from '@freelancer/location';
import { LOGIN_CONFIG } from '@freelancer/login';
import {
  ARROW_LOGIN_SIGNUP_MARKETING_CONFIG,
  LoginSignupModule,
  LoginSignupService,
} from '@freelancer/login-signup';
import { LoginSignupTesting } from '@freelancer/login-signup/testing';
import { FREELANCER_PWA_TRACKING_PROVIDER, Pwa } from '@freelancer/pwa';
import { SEO_CONFIG } from '@freelancer/seo';
import { ThreatmetrixModule } from '@freelancer/threatmetrix';
import {
  CustomErrorHandler,
  ErrorTracking,
  FacebookPixelTracking,
  GoogleTracking,
  PerformanceTracking,
  Tracking,
  TrackingConsent,
  TrackingModule,
} from '@freelancer/tracking';
import {
  ErrorTrackingTesting,
  FacebookPixelTrackingTesting,
  GoogleTrackingTesting,
  PerformanceTrackingTesting,
  TrackingTesting,
} from '@freelancer/tracking/testing';
import { UiModule } from '@freelancer/ui';
import { ModalModule } from '@freelancer/ui/modal';
import { UiModalsModule } from '@freelancer/ui/ui-modals.module';
import { CookieModule, COOKIE_OPTIONS } from '@laurentgoudet/ngx-cookie';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { ActionSanitizer } from '@ngrx/store-devtools/src/config';
import { getDomainsMap } from 'applications';
import { environment } from '../environments/environment';
import { AppModalsModule } from './app-modals.module';
import { cookieOptionsFactory } from './cookie-options.factory';

/* The type for ActionSanitizer doesn't allow a custom action type.
 * ActionSanitizer probably should be
 *  export declare type ActionSanitizer<A extends Action = Action> = (
 *    action: A,
 *    id: number,
 *  ) => A;
 * but it isn't so let's cast through `any` for now.
 */
const actionSanitizer: ActionSanitizer = datastoreActionSanitizer as any;

// /!\ DO NOT ADD ANYTHING IN THERE WITHOUT TALKING TO FRONTEND INFRA FIRST /!\
// Application-wide code should be avoided as much as possible; does your code
// needs:
// - to be loaded on ALL logged-in pages?
// - AND to be loaded on ALL logged-out pages?
// - AND to be loaded on the Admin?
// - ...
// Probably not.
@NgModule({
  imports: [
    AuthModule,
    BrowserModule.withServerTransition({ appId: 'webapp' }),
    BrowserTransferStateModule,
    BrowserAnimationsModule,
    FormsModule,
    HttpClientXsrfModule.disable(), // no part of the app should rely on that
    AppModalsModule,
    ComponentsModalsModule,
    InAppBrowserModule,
    UiModalsModule,
    GoogleMapsModule.forRoot({
      apiKey: environment.mapsConfig.apiKey,
      libraries: ['places'],
    }),
    UiModule.initialize({
      assetsBaseUrl: environment.uiConfig.assetsBaseUrl,
      theme: environment.uiConfig.theme,
    }),
    ModalModule,
    CookieModule.forRoot(),
    ThreatmetrixModule.initialize({
      profilingDomain: environment.threatmetrixConfig.profilingDomain,
      orgId: environment.threatmetrixConfig.orgId,
    }),
    environment.useFakes
      ? DatastoreFakeModule.initialize({
          debug: false,
          initializer: datastoreInitializer(),
        })
      : DatastoreModule.initialize({
          webSocketUrl: environment.datastoreConfig.webSocketUrl,
          enableStoreFreeze: environment.datastoreConfig.enableStoreFreeze,
          httpAdapter: FreelancerHttp,
          requestData: {},
        }),
    !environment.production
      ? StoreDevtoolsModule.instrument({
          maxAge: 150,
          actionSanitizer,
          actionsBlocklist: ['LOCAL_CACHE_FETCH_SUCCESS'],
        })
      : [],
    TrackingModule.initialize({
      trackingEndpoint: environment.trackingConfig.trackingEndpoint,
      sessionCookie: environment.trackingConfig.sessionCookie,
      clientKey: environment.trackingConfig.clientKey,
      navigationPerformanceSamplingRate:
        environment.trackingConfig.navigationPerformanceSamplingRate,
      enableErrorTracking: environment.trackingConfig.enableErrorTracking,
      sentryDsn: environment.trackingConfig.sentryDsn,
      kibanaBaseUrl: environment.trackingConfig.kibanaBaseUrl,
      gaTrackingId: environment.trackingConfig.gaTrackingId,
      awGoogleConversionId: environment.trackingConfig.awGoogleConversionId,
      facebookPixelId: environment.trackingConfig.facebookPixelId,
      tracesSampleRate: environment.trackingConfig.tracesSampleRate,
      gaCustomDimensionMap: {
        dimension1: environment.trackingConfig.gaCustomDimensionMap.dimension1,
        dimension2: environment.trackingConfig.gaCustomDimensionMap.dimension2,
        dimension3: environment.trackingConfig.gaCustomDimensionMap.dimension3,
      },
      outbrainPixelId: environment.trackingConfig.outbrainPixelId,
      gAdsTrackingId: environment.trackingConfig.gaTrackingId,
      gAdsAllowedDomain: environment.trackingConfig.gAdsAllowedDomain,
    }),
    FileUploadLegacyModule,
    LoginSignupModule,
    FileDownloadModule,
    FileViewerModule,
  ],
  providers: [
    {
      provide: AUTH_CONFIG,
      useValue: {
        baseUrl: environment.authConfig.baseUrl,
        switchAccountHackyEndpoint:
          environment.authConfig.switchAccountHackyEndpoint,
        authHashCookie: environment.authConfig.authHashCookie,
        userIdCookie: environment.authConfig.userIdCookie,
        csrfCookie: environment.authConfig.csrfCookie,
        gdprCookie: environment.authConfig.gdprCookie,
        authHeaderName: environment.authConfig.authHeaderName,
        referralCookie: environment.authConfig.referralCookie,
        authIosKeychainToken: environment.authConfig.authIosKeychainToken,
        arrowFedCookie: environment.authConfig.arrowFedCookie,
      },
    },
    environment.useFakes
      ? [
          { provide: ABTest, useClass: ABTestTesting },
          { provide: Auth, useClass: AuthTesting },
          {
            provide: CustomFieldsValidator,
            useClass: CustomFieldsValidatorTesting,
          },
          { provide: Tracking, useClass: TrackingTesting },
          { provide: ErrorTracking, useClass: ErrorTrackingTesting },
          {
            provide: PerformanceTracking,
            useClass: PerformanceTrackingTesting,
          },
          { provide: GoogleTracking, useClass: GoogleTrackingTesting },
          { provide: LoginSignupService, useClass: LoginSignupTesting },
          {
            provide: FacebookPixelTracking,
            useClass: FacebookPixelTrackingTesting,
          },
          {
            provide: SENTRY_TRACING_PROVIDER,
            useClass: ErrorTrackingTesting,
          },
        ]
      : [],
    { provide: ErrorHandler, useClass: CustomErrorHandler },
    {
      provide: CAPTCHA_CONFIG,
      useValue: {
        recaptchaPublicKey: environment.captchaConfig.recaptchaPublicKey,
      },
    },
    {
      provide: COOKIE_OPTIONS,
      deps: [PLATFORM_ID, Location],
      useFactory: cookieOptionsFactory,
    },
    { provide: USE_FAKES, useValue: environment.useFakes },
    { provide: SITE_NAME, useValue: environment.siteName },
    {
      provide: SEO_CONFIG,
      useValue: {
        linkTags: {
          rss: {
            rel: environment.seoConfig.linkTags.rss.rel,
            type: environment.seoConfig.linkTags.rss.type,
            title: environment.seoConfig.linkTags.rss.title,
            href: environment.seoConfig.linkTags.rss.href,
          },
        },
        metaTags: {
          robots: {
            name: environment.seoConfig.metaTags.robots.name,
            content: environment.seoConfig.metaTags.robots.content,
          },
          alexa: {
            name: environment.seoConfig.metaTags.alexa.name,
            content: environment.seoConfig.metaTags.alexa.content,
          },
          bing: {
            name: environment.seoConfig.metaTags.bing.name,
            content: environment.seoConfig.metaTags.bing.content,
          },
        },
        defaultMetaImage: environment.seoConfig.defaultMetaImage,
      },
    },
    {
      provide: GOOGLE_MAPS_CONFIG,
      useValue: {
        apiKey: environment.mapsConfig.apiKey,
      },
    },
    {
      provide: APPS_DOMAINS_MAP,
      useValue: getDomainsMap(environment.environmentName),
    },
    { provide: ENVIRONMENT_NAME, useValue: environment.environmentName },
    { provide: APP_NAME, useValue: environment.appName },
    {
      provide: FREELANCER_HTTP_CONFIG,
      useValue: {
        baseUrl: environment.freelancerHttpConfig.baseUrl,
        apiBaseUrl: environment.freelancerHttpConfig.apiBaseUrl,
        ajaxBaseUrl: environment.freelancerHttpConfig.ajaxBaseUrl,
        authBaseUrl: environment.freelancerHttpConfig.authBaseUrl,
        fileServiceBaseUrl: environment.freelancerHttpConfig.fileServiceBaseUrl,
      },
    },
    { provide: FREELANCER_HTTP_AUTH_PROVIDERS, useExisting: Auth, multi: true },
    {
      provide: FREELANCER_HTTP_ABTEST_OVERRIDES_PROVIDER,
      useExisting: ABTestOverrides,
    },
    {
      provide: FREELANCER_HTTP_TRACKING_PROVIDER,
      useExisting: Tracking,
    },
    {
      provide: FREELANCER_LOCATION_AUTH_PROVIDER,
      useExisting: Auth,
    },
    {
      provide: FREELANCER_LOCATION_HTTP_BASE_URL_PROVIDER,
      useValue: environment.freelancerHttpConfig.baseUrl,
    },
    {
      provide: FREELANCER_LOCATION_PWA_PROVIDER,
      useExisting: Pwa,
    },
    {
      provide: FREELANCER_LOCATION_TRACKING_CONSENT_PROVIDER,
      useExisting: TrackingConsent,
    },
    {
      provide: FREELANCER_PWA_TRACKING_PROVIDER,
      useExisting: Tracking,
    },
    {
      provide: FREELANCER_LOCATION_IN_APP_BROWSER_PROVIDER,
      useExisting: InAppBrowser,
    },
    {
      provide: SENTRY_TRACING_PROVIDER,
      useClass: ErrorTracking,
    },
    { provide: ABTEST_WHITELIST_COOKIE, useValue: environment.whitelistCookie },
    {
      provide: ARROW_LOGIN_SIGNUP_MARKETING_CONFIG,
      useValue: {
        arrowMarketingEndpoint:
          environment.arrowLoginSignupMarketingConfig.arrowMarketingEndpoint,
        userIdCookie: environment.arrowLoginSignupMarketingConfig.userIdCookie,
        languageCookie:
          environment.arrowLoginSignupMarketingConfig.languageCookie,
      },
    },
    {
      provide: FACEBOOK_CONFIG,
      useValue: {
        appId: environment.facebookConfig.appId,
      },
    },
    {
      provide: BILLING_CONFIG,
      useValue: {
        baseUrl: environment.billingConfig.baseUrl,
      },
    },
    { provide: LANGUAGE_COOKIE, useValue: environment.languageCookie },
    {
      provide: LOGIN_CONFIG,
      useValue: {
        loggedOutRestricted: environment.loginConfig.loggedOutRestricted,
        defaultLogoutRedirectUrl:
          environment.loginConfig.defaultLogoutRedirectUrl,
        ssoIntegration: environment.loginConfig.ssoIntegration,
      },
    },
    BackgroundGeolocation,
  ],
  exports: [BrowserModule, ModalModule, TrackingModule, UiModule],
})
export class AppCommonModule {}
